import { useState, useEffect, useCallback } from 'react';
import type { Diagnosis } from '../types/diagnosis';
import { subscribeToDiagnoses, getDiagnosis } from '../services/diagnosis';

interface UseDiagnosesResult {
  diagnoses: Diagnosis[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to subscribe to real-time diagnosis updates for a plant
 */
export function useDiagnoses(userId: string | null, plantId: string | null): UseDiagnosesResult {
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!userId || !plantId) {
      setDiagnoses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToDiagnoses(
      userId,
      plantId,
      (newDiagnoses) => {
        setDiagnoses(newDiagnoses);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load diagnoses');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, plantId, refreshKey]);

  return { diagnoses, loading, error, refetch };
}

interface UseDiagnosisResult {
  diagnosis: Diagnosis | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch a single diagnosis
 */
export function useDiagnosis(
  userId: string | null,
  plantId: string | null,
  diagnosisId: string | null
): UseDiagnosisResult {
  const [diagnosis, setDiagnosis] = useState<Diagnosis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!userId || !plantId || !diagnosisId) {
      setDiagnosis(null);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    getDiagnosis(userId, plantId, diagnosisId)
      .then((result) => {
        if (mounted) {
          setDiagnosis(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err.message || 'Failed to load diagnosis');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [userId, plantId, diagnosisId, refreshKey]);

  return { diagnosis, loading, error, refetch };
}
